import { useState, useEffect, useRef } from 'react';
import { X, Copy, Trash2, ChevronRight, ChevronDown, AlertCircle, Clock, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

interface FailedRequest {
  id: string;
  timestamp: Date;
  method: string;
  url: string;
  status?: number;
  statusText?: string;
  errorMessage: string;
  requestData?: any;
  responseData?: any;
  headers?: any;
}

let loggerInstance: ((log: FailedRequest) => void) | null = null;

// Export function to log from anywhere
export const logFailedRequest = (log: Omit<FailedRequest, 'id' | 'timestamp'>) => {
  if (loggerInstance) {
    loggerInstance({
      ...log,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date()
    });
  }
};

export function DebugLogger() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [logs, setLogs] = useState<FailedRequest[]>([]);
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());
  const logsEndRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);

  // Only show in development mode
  if (typeof import.meta === 'undefined' || !import.meta.env?.DEV) {
    return null;
  }

  // Register the logger instance
  useEffect(() => {
    loggerInstance = (log: FailedRequest) => {
      setLogs(prev => [...prev, log]);
    };

    return () => {
      loggerInstance = null;
    };
  }, []);

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (autoScroll && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, autoScroll]);

  const toggleExpanded = (logId: string) => {
    setExpandedLogs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  const copyAllLogs = () => {
    const logText = logs.map(log => {
      const time = log.timestamp.toLocaleTimeString();
      return `[${time}] ${log.method} ${log.url}
Status: ${log.status || 'N/A'} ${log.statusText || ''}
Error: ${log.errorMessage}
Request Data: ${JSON.stringify(log.requestData, null, 2)}
Response Data: ${JSON.stringify(log.responseData, null, 2)}
-------------------`;
    }).join('\n\n');

    navigator.clipboard.writeText(logText);
    toast.success('All logs copied to clipboard!');
  };

  const copyLog = (log: FailedRequest) => {
    const time = log.timestamp.toLocaleTimeString();
    const logText = `[${time}] ${log.method} ${log.url}
Status: ${log.status || 'N/A'} ${log.statusText || ''}
Error: ${log.errorMessage}
Request Data: ${JSON.stringify(log.requestData, null, 2)}
Response Data: ${JSON.stringify(log.responseData, null, 2)}`;

    navigator.clipboard.writeText(logText);
    toast.success('Log copied to clipboard!');
  };

  const clearLogs = () => {
    setLogs([]);
    setExpandedLogs(new Set());
    toast.success('Logs cleared');
  };

  const formatTimestamp = (date: Date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit',
      hour12: false 
    });
  };

  const getStatusColor = (status?: number) => {
    if (!status) return 'text-gray-500';
    if (status >= 500) return 'text-red-600';
    if (status >= 400) return 'text-orange-600';
    return 'text-gray-600';
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 z-50 transition-all"
        title="Open Debug Logger"
      >
        <AlertCircle className="h-5 w-5" />
        <span className="font-medium">Debug Logger</span>
        {logs.length > 0 && (
          <span className="bg-white text-red-600 px-2 py-0.5 rounded-full text-xs font-bold">
            {logs.length}
          </span>
        )}
      </button>
    );
  }

  return (
    <div className={`fixed right-0 top-0 h-full bg-white shadow-2xl z-50 flex flex-col border-l-2 border-red-500 transition-all ${
      isMinimized ? 'w-12' : 'w-full md:w-[500px] lg:w-[600px]'
    }`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-rose-600 text-white p-4 flex items-center justify-between flex-none">
        {!isMinimized && (
          <>
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              <h2 className="font-bold">Failed API Requests</h2>
              <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">
                {logs.length}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsMinimized(true)}
                className="hover:bg-white/20 p-1 rounded transition-colors"
                title="Minimize"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="hover:bg-white/20 p-1 rounded transition-colors"
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </>
        )}
        {isMinimized && (
          <button
            onClick={() => setIsMinimized(false)}
            className="w-full h-full flex items-center justify-center hover:bg-white/20 transition-colors"
            title="Expand"
          >
            <ChevronRight className="h-5 w-5 rotate-180" />
          </button>
        )}
      </div>

      {!isMinimized && (
        <>
          {/* Actions Bar */}
          <div className="bg-gray-50 border-b border-gray-200 p-3 flex items-center justify-between gap-2 flex-none">
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoScroll}
                  onChange={(e) => setAutoScroll(e.target.checked)}
                  className="rounded border-gray-300"
                />
                Auto-scroll
              </label>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={copyAllLogs}
                disabled={logs.length === 0}
                className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded text-sm transition-colors"
                title="Copy all logs"
              >
                <Copy className="h-4 w-4" />
                Copy All
              </button>
              <button
                onClick={clearLogs}
                disabled={logs.length === 0}
                className="flex items-center gap-1 px-3 py-1.5 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded text-sm transition-colors"
                title="Clear logs"
              >
                <Trash2 className="h-4 w-4" />
                Clear
              </button>
            </div>
          </div>

          {/* Logs List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {logs.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <AlertCircle className="h-12 w-12 mb-2" />
                <p className="text-sm">No failed requests logged yet</p>
                <p className="text-xs mt-1">Failed API calls will appear here</p>
              </div>
            ) : (
              logs.map((log) => {
                const isExpanded = expandedLogs.has(log.id);
                
                return (
                  <div
                    key={log.id}
                    className="border border-gray-200 rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow"
                  >
                    {/* Log Header */}
                    <div
                      onClick={() => toggleExpanded(log.id)}
                      className="p-3 cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-2 flex-1 min-w-0">
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-gray-400 mt-0.5 flex-none" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-gray-400 mt-0.5 flex-none" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-mono text-xs font-bold bg-red-100 text-red-700 px-2 py-0.5 rounded">
                                {log.method}
                              </span>
                              <span className={`font-mono text-xs font-bold px-2 py-0.5 rounded ${
                                log.status ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-700'
                              }`}>
                                {log.status || 'ERR'}
                              </span>
                              <span className="flex items-center gap-1 text-xs text-gray-500">
                                <Clock className="h-3 w-3" />
                                {formatTimestamp(log.timestamp)}
                              </span>
                            </div>
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {log.url}
                            </div>
                            <div className="text-xs text-red-600 mt-1 line-clamp-2">
                              {log.errorMessage}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            copyLog(log);
                          }}
                          className="flex-none p-1.5 hover:bg-gray-200 rounded transition-colors"
                          title="Copy this log"
                        >
                          <Copy className="h-4 w-4 text-gray-600" />
                        </button>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="border-t border-gray-200 p-3 bg-gray-50 space-y-3">
                        {/* Full URL */}
                        <div>
                          <div className="text-xs font-semibold text-gray-700 mb-1">Full URL</div>
                          <div className="bg-white p-2 rounded border border-gray-200 text-xs font-mono break-all">
                            {log.url}
                          </div>
                        </div>

                        {/* Error Message */}
                        <div>
                          <div className="text-xs font-semibold text-gray-700 mb-1">Error Message</div>
                          <div className="bg-red-50 p-2 rounded border border-red-200 text-xs text-red-700">
                            {log.errorMessage}
                          </div>
                        </div>

                        {/* Request Data */}
                        {log.requestData && (
                          <div>
                            <div className="text-xs font-semibold text-gray-700 mb-1">Request Data</div>
                            <pre className="bg-white p-2 rounded border border-gray-200 text-xs overflow-x-auto">
                              {JSON.stringify(log.requestData, null, 2)}
                            </pre>
                          </div>
                        )}

                        {/* Response Data */}
                        {log.responseData && (
                          <div>
                            <div className="text-xs font-semibold text-gray-700 mb-1">Response Data</div>
                            <pre className="bg-white p-2 rounded border border-gray-200 text-xs overflow-x-auto">
                              {JSON.stringify(log.responseData, null, 2)}
                            </pre>
                          </div>
                        )}

                        {/* Headers */}
                        {log.headers && (
                          <div>
                            <div className="text-xs font-semibold text-gray-700 mb-1">Request Headers</div>
                            <pre className="bg-white p-2 rounded border border-gray-200 text-xs overflow-x-auto">
                              {JSON.stringify(log.headers, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
            <div ref={logsEndRef} />
          </div>
        </>
      )}
    </div>
  );
}